import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PurchasingService } from '../../../services/purchasing.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-no-po-items-modal',
	templateUrl: './no-po-items-modal.component.html',
	styleUrls: ['./no-po-items-modal.component.scss']
})
export class NoPoItemsModalComponent implements OnInit, OnDestroy {

	stockmovements: any[] = [];
	filteredStockmovements: any[] = [];
	loading = false;
	processing = false;
	selectedItems: Set<number> = new Set();
	private subscriptions: Subscription[] = [];

	searchTerm = '';
	dateFrom: Date | null = null;
	dateTo: Date | null = null;
	selectedLocation = '';
	priceMin: number | string | null = null;
	priceMax: number | string | null = null;
	availableLocations: string[] = [];

	groups: Array<{id: string, poNumber: string}> = [];
	itemGroups: Map<number, string | null> = new Map(); // stkmoveno -> groupId (null = individual)
	private groupCounter = 1;
	activeGroupId: string | null = null;

	get dateFromString(): string {
		return this.dateFrom ? this.dateFrom.toISOString().split('T')[0] : '';
	}

	get dateToString(): string {
		return this.dateTo ? this.dateTo.toISOString().split('T')[0] : '';
	}

	editablePrices: Map<number, number> = new Map();

	constructor(
		public dialogRef: MatDialogRef<NoPoItemsModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: { supplierid: string },
		private purchasingService: PurchasingService,
		private globalSearchService: GlobalSearchService
	) { }

	ngOnInit(): void {
		const today = new Date();
		this.dateTo = today;
		this.dateFrom = new Date(today.getFullYear(), today.getMonth() - 1, 1);
		this.loadData();
	}

	addGroup(): void {
		const groupId = `group_${this.groupCounter++}`;
		this.groups.push({
			id: groupId,
			poNumber: ''
		});
	}

	removeGroup(index: number): void {
		const group = this.groups[index];
		if (group) {
			// Remove all items assigned to this group
			this.itemGroups.forEach((groupId, stkmoveno) => {
				if (groupId === group.id) {
					this.itemGroups.delete(stkmoveno);
				}
			});
			this.groups.splice(index, 1);
		}
	}

	getGroupItemCount(groupId: string): number {
		let count = 0;
		this.itemGroups.forEach((assignedGroupId) => {
			if (assignedGroupId === groupId) {
				count++;
			}
		});
		return count;
	}

	getAvailableGroupsForSelect(): Array<{id: string, label: string}> {
		return this.groups.map((group, index) => ({
			id: group.id,
			label: `GRN ${index + 1}${group.poNumber ? ' (' + group.poNumber + ')' : ''}`
		}));
	}

	getDateFilterParams(): { dateFrom?: string; dateTo?: string } {
		const filters: { dateFrom?: string; dateTo?: string } = {};
		const toYmd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
		if (this.dateFrom && !isNaN(this.dateFrom.getTime())) {
			filters.dateFrom = toYmd(this.dateFrom);
		}
		if (this.dateTo && !isNaN(this.dateTo.getTime())) {
			filters.dateTo = toYmd(this.dateTo);
		}
		return filters;
	}

	onDateFilterChange(): void {
		this.loadData();
	}

	loadData(): void {
		this.loading = true;
		const filters = this.getDateFilterParams();

		const stockMovementsSub = this.purchasingService.getStockMovementsWithoutPO(this.data.supplierid, 1000, Object.keys(filters).length ? filters : undefined).subscribe(
			(result: any) => {
				let data = result;
				
				if (result && typeof result === 'object' && !Array.isArray(result)) {
					if (result.data && Array.isArray(result.data)) {
						data = result.data;
					} else if (result.rows && Array.isArray(result.rows)) {
						data = result.rows;
					} else if (result.results && Array.isArray(result.results)) {
						data = result.results;
					}
				}
				
				this.stockmovements = Array.isArray(data) ? data : [];

				this.availableLocations = [...new Set(this.stockmovements.map(sm => sm.loccode).filter(loc => loc))];

				this.stockmovements.forEach(sm => {
					const price = parseFloat(sm.price) || 0;
					sm.editablePrice = Math.round(price * 100) / 100;
					this.editablePrices.set(sm.stkmoveno, sm.editablePrice);
				});

				this.applyFilters();
				this.loading = false;
			},
			(error: any) => {
				console.error('Error loading stockmovements:', error);
				this.globalSearchService.showNotification('Error loading stockmovements', 'error', 'bottom', 'right');
				this.loading = false;
			}
		);
		this.subscriptions.push(stockMovementsSub);

	}

	ngOnDestroy(): void {
		this.subscriptions.forEach(sub => {
			if (sub && !sub.closed) {
				sub.unsubscribe();
			}
		});
	}

	applyFilters(): void {
		let filtered = [...this.stockmovements];

		if (this.searchTerm && this.searchTerm.trim() !== '') {
			const term = this.searchTerm.toLowerCase().trim();
			filtered = filtered.filter(sm =>
				(sm.stockid && sm.stockid.toLowerCase().includes(term)) ||
				(sm.description && sm.description.toLowerCase().includes(term)) ||
				(sm.locationname && sm.locationname.toLowerCase().includes(term)) ||
				(sm.loccode && sm.loccode.toLowerCase().includes(term)) ||
				(sm.debtorno && sm.debtorno.toLowerCase().includes(term)) ||
				(sm.reference && sm.reference.toLowerCase().includes(term))
			);
		}

		if (this.selectedLocation && this.selectedLocation !== '') {
			filtered = filtered.filter(sm => sm.loccode === this.selectedLocation);
		}

		if (this.priceMin !== null && this.priceMin !== undefined && this.priceMin !== '' && this.priceMin !== 0) {
			const minPrice = typeof this.priceMin === 'string' ? parseFloat(this.priceMin) : this.priceMin;
			if (!isNaN(minPrice) && minPrice > 0) {
				filtered = filtered.filter(sm => {
					const price = sm.editablePrice !== undefined ? sm.editablePrice : (parseFloat(sm.price) || 0);
					return price >= minPrice;
				});
			}
		}

		if (this.priceMax !== null && this.priceMax !== undefined && this.priceMax !== '' && this.priceMax !== 0) {
			const maxPrice = typeof this.priceMax === 'string' ? parseFloat(this.priceMax) : this.priceMax;
			if (!isNaN(maxPrice) && maxPrice > 0) {
				filtered = filtered.filter(sm => {
					const price = sm.editablePrice !== undefined ? sm.editablePrice : (parseFloat(sm.price) || 0);
					return price <= maxPrice;
				});
			}
		}

		this.filteredStockmovements = filtered;

		this.selectedItems.forEach(stkmoveno => {
			if (!this.filteredStockmovements.some(sm => sm.stkmoveno === stkmoveno)) {
				this.selectedItems.delete(stkmoveno);
			}
		});
	}

	clearFilters(): void {
		this.searchTerm = '';
		this.dateFrom = null;
		this.dateTo = null;
		this.selectedLocation = '';
		this.priceMin = null;
		this.priceMax = null;
		this.loadData();
	}

	normalizePriceFilter(type: 'min' | 'max'): void {
		if (type === 'min') {
			if (this.priceMin === '' || this.priceMin === null || this.priceMin === undefined) {
				this.priceMin = null;
			} else if (typeof this.priceMin === 'string') {
				const parsed = parseFloat(this.priceMin);
				this.priceMin = isNaN(parsed) ? null : parsed;
			}
		} else {
			if (this.priceMax === '' || this.priceMax === null || this.priceMax === undefined) {
				this.priceMax = null;
			} else if (typeof this.priceMax === 'string') {
				const parsed = parseFloat(this.priceMax);
				this.priceMax = isNaN(parsed) ? null : parsed;
			}
		}
		this.applyFilters();
	}

	toggleSelection(stkmoveno: number): void {
		if (this.selectedItems.has(stkmoveno)) {
			this.selectedItems.delete(stkmoveno);
			// Remove from group when unselected
			this.itemGroups.delete(stkmoveno);
		} else {
			this.selectedItems.add(stkmoveno);
			// Automatically assign to active GRN if one is selected
			if (this.activeGroupId) {
				this.itemGroups.set(stkmoveno, this.activeGroupId);
			}
		}
	}

	isSelected(stkmoveno: number): boolean {
		return this.selectedItems.has(stkmoveno);
	}

	toggleAll(): void {
		if (this.selectedItems.size === this.filteredStockmovements.length && this.filteredStockmovements.length > 0) {
			// Unselect all and remove from groups
			this.selectedItems.clear();
			this.filteredStockmovements.forEach(sm => {
				this.itemGroups.delete(sm.stkmoveno);
			});
		} else {
			// Select all and assign to active GRN if one is selected
			this.filteredStockmovements.forEach(sm => {
				this.selectedItems.add(sm.stkmoveno);
				if (this.activeGroupId) {
					this.itemGroups.set(sm.stkmoveno, this.activeGroupId);
				}
			});
		}
	}

	setActiveGroup(groupId: string | null): void {
		this.activeGroupId = groupId;
	}

	isActiveGroup(groupId: string): boolean {
		return this.activeGroupId === groupId;
	}

	updatePrice(sm: any, event: any): void {
		const newPrice = parseFloat(event.target.value);
		if (!isNaN(newPrice) && newPrice >= 0) {
			const roundedPrice = Math.round(newPrice * 100) / 100;
			sm.editablePrice = roundedPrice;
			this.editablePrices.set(sm.stkmoveno, roundedPrice);
			event.target.value = roundedPrice.toFixed(2);
		} else {
			const defaultPrice = sm.editablePrice || sm.price || 0;
			const roundedDefault = Math.round(defaultPrice * 100) / 100;
			event.target.value = roundedDefault.toFixed(2);
		}
	}

	validateSelection(): boolean {
		if (this.selectedItems.size === 0) {
			this.globalSearchService.showNotification('Please select at least one item', 'warning', 'bottom', 'right');
			return false;
		}

		// Validate that all selected items are assigned to groups with PO numbers
		const selectedMovements = this.filteredStockmovements.filter(sm => this.selectedItems.has(sm.stkmoveno));

		// Check if any selected item is not assigned to a group
		const ungroupedItems = selectedMovements.filter(sm => {
			const groupId = this.getItemGroup(sm.stkmoveno);
			return !groupId;
		});

		if (ungroupedItems.length > 0) {
			this.globalSearchService.showNotification('All selected items must be assigned to a GRN', 'error', 'bottom', 'right');
			return false;
		}

		// Validate that all GRNs with assigned items have PO numbers
		const groupsWithItems = new Set<string>();
		selectedMovements.forEach(sm => {
			const groupId = this.getItemGroup(sm.stkmoveno);
			if (groupId) {
				groupsWithItems.add(groupId);
			}
		});

		for (const groupId of groupsWithItems) {
			const group = this.groups.find(g => g.id === groupId);
			if (!group || !group.poNumber || group.poNumber.trim() === '') {
				this.globalSearchService.showNotification('All GRNs must have a New PO Number', 'error', 'bottom', 'right');
				return false;
			}
		}

		for (const sm of selectedMovements) {
			const price = sm.editablePrice || sm.price || 0;
			if (price < 0) {
				this.globalSearchService.showNotification(`Invalid price for item ${sm.stockid}`, 'error', 'bottom', 'right');
				return false;
			}
		}

		if (this.dateFrom && this.dateTo && this.dateFrom > this.dateTo) {
			this.globalSearchService.showNotification('Date range is invalid: From date must be before To date', 'error', 'bottom', 'right');
			return false;
		}

		if (this.priceMin !== null && this.priceMax !== null && this.priceMin > this.priceMax) {
			this.globalSearchService.showNotification('Price range is invalid: Min price must be less than Max price', 'error', 'bottom', 'right');
			return false;
		}

		return true;
	}

	addSelectedToInvoice(): void {
		if (!this.validateSelection()) {
			return;
		}

		this.processing = true;

		// Group items by their groupId
		const grouped = this.getGroupedItems();

		// Build groups array for API
		const groups: Array<{groupId: string | null, items: Array<{stkmoveno: number, unitprice: number}>}> = [];

		grouped.forEach((items, groupId) => {
			groups.push({
				groupId: groupId,
				items: items
			});
		});

		// Build groups with PO numbers
		const groupsWithPONumbers: Array<{groupId: string | null, poNumber: string | undefined, items: Array<{stkmoveno: number, unitprice: number}>}> = [];

		grouped.forEach((items, groupId) => {
			const group = groupId ? this.groups.find(g => g.id === groupId) : null;
			groupsWithPONumbers.push({
				groupId: groupId,
				poNumber: group ? group.poNumber : undefined,
				items: items
			});
		});

		const batchData = {
			supplierid: this.data.supplierid,
			groups: groupsWithPONumbers
		};

		this.purchasingService.linkStockMovementsToPOBatch(batchData).subscribe(
			(result: any) => {
				this.processing = false;
				if (result && result.success) {
					const createdPOs = result.createdPOs || [];
					const createdGRNs = result.createdGRNs || [];
					const totalItems = this.selectedItems.size;
					this.globalSearchService.showNotification(`${totalItems} item(s) added to invoice successfully`, 'success', 'bottom', 'right');
					this.dialogRef.close({
						success: true,
						createdPOs: createdPOs,
						createdGRNs: createdGRNs
					});
				} else {
					this.globalSearchService.showNotification(result?.message || 'Failed to add items to invoice', 'error', 'bottom', 'right');
				}
			},
			(error: any) => {
				this.processing = false;
				console.error('Error linking stock movements:', error);
				this.globalSearchService.showNotification('Error processing items', 'error', 'bottom', 'right');
			}
		);
	}

	cancel(): void {
		this.dialogRef.close();
	}

	get resultsCount(): number {
		return this.filteredStockmovements.length;
	}

	get selectedCount(): number {
		return this.selectedItems.size;
	}

	setItemGroup(stkmoveno: number, groupId: string | null | undefined): void {
		if (groupId && groupId.trim() !== '') {
			this.itemGroups.set(stkmoveno, groupId);
		} else {
			this.itemGroups.delete(stkmoveno);
		}
	}

	getItemGroup(stkmoveno: number): string | null {
		return this.itemGroups.get(stkmoveno) || null;
	}

	assignSelectedToGroup(groupId: string): void {
		this.selectedItems.forEach(stkmoveno => {
			this.itemGroups.set(stkmoveno, groupId);
		});
	}

	clearSelectedGroups(): void {
		this.selectedItems.forEach(stkmoveno => {
			this.itemGroups.delete(stkmoveno);
		});
	}

	getGroupedItems(): Map<string | null, Array<{stkmoveno: number, unitprice: number}>> {
		const grouped = new Map<string | null, Array<{stkmoveno: number, unitprice: number}>>();
		const selectedMovements = this.filteredStockmovements.filter(sm => this.selectedItems.has(sm.stkmoveno));

		selectedMovements.forEach(sm => {
			const groupId = this.getItemGroup(sm.stkmoveno);
			if (!grouped.has(groupId)) {
				grouped.set(groupId, []);
			}
			grouped.get(groupId)!.push({
				stkmoveno: sm.stkmoveno,
				unitprice: sm.editablePrice || sm.price || 0
			});
		});

		return grouped;
	}
}
